import React, { createContext, useContext, useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryObserverResult,
} from '@tanstack/react-query';
import type { LoginRequestDto } from '@evimo/client';
import type { CurrentUser } from '@evimo/types';
import { AuthService } from '~/app/auth/services/auth.service';

interface AuthContextType {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequestDto) => Promise<void>;
  logout: () => Promise<void>;
  refetchUser: () => Promise<
    QueryObserverResult<CurrentUser | null | undefined, Error>
  >;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: me,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: AuthService.getCurrentUser,
  });

  const loginMutation = useMutation({
    mutationFn: (payload: LoginRequestDto) => AuthService.login(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => AuthService.logout(),
    onSuccess: () => {
      queryClient.clear();
    },
  });

  const value = useMemo<AuthContextType>(
    () => ({
      user: me ?? null,
      isAuthenticated: !!me,
      isLoading,
      login: async (data: LoginRequestDto) => {
        await loginMutation.mutateAsync(data);
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      refetchUser: () => refetch(),
    }),
    [me, isLoading, loginMutation, logoutMutation, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
